import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Drawer from 'components/common/drawer'
import Detail from 'components/common/details/Detail'
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Divider, message, Popconfirm, Space, Table, Tabs, Tag } from 'antd'
import useRequest from 'components/common/hooks/useRequest'
import dayjs from 'dayjs'
import Ret00cForm from './Ret00cForm'
import { destroyRet00c, getRet00cDetail } from 'components/common/services/ret00cs'
import { useRet00cGroupFilter } from './useRet00cGroupFilter'
import { formatJsonText, formatRet00cExampleFields } from './jsonExampleUtils'
import Ret00cActionBar, {
  RET00C_ACTION_BAR_GROUP_CLASS_NAME,
  RET00C_ACTION_BAR_LAYOUT_CLASS_NAME,
  RET00C_ACTION_BAR_WRAPPER_CLASS_NAME,
  RET00C_ACTION_BAR_WRAPPER_STYLE,
} from './Ret00cActionBar'

interface Ret00cDetailProps {
  id: string
  open?: boolean
  onClose?: () => void
  mode?: 'drawer' | 'page'
}

const flattenParams = (items: any[], parentId: number | null = null, level = 0): any[] =>
  items
    .filter((item) => (item.parent_id || null) === parentId)
    .flatMap((item) => ([
      { ...item, level },
      ...flattenParams(items, item.id, level + 1),
    ]))

const dataColumns = [
  { title: '接口名称', dataIndex: 'name' },
  { title: '接口分组', dataIndex: 'group_path_name', span: 2 },
  { title: '请求方式', dataIndex: 'http_method' },
  { title: '接口路径', dataIndex: 'path', span: 2 },
]

function Ret00cDetail({ id, open = false, onClose, mode = 'drawer' }: Ret00cDetailProps): JSX.Element {
  const navigate = useNavigate()
  const { reloadTree } = useRet00cGroupFilter()
  const [detailRecord, setDetailRecord] = useState<any>(null)
  const [ret00cas, setRet00cas] = useState<any[]>([])
  const [ret00ccs, setRet00ccs] = useState<any[]>([])
  const [destroying, setDestroying] = useState(false)
  const [requestTabKey, setRequestTabKey] = useState('request-params')
  const [responseTabKey, setResponseTabKey] = useState('response-params')

  const { run: getRet00cDetailRequest } = useRequest<{ data: any }>(() => getRet00cDetail(id), {
    refreshDeps: [id],
    ready: !!id,
    onSuccess: (res) => {
      setDetailRecord(res?.data?.item)
      setRet00cas(res?.data?.ret00cas || [])
      setRet00ccs(res?.data?.ret00ccs || [])
    },
    manual: true,
  })

  useEffect(() => {
    if ((mode === 'page' || open) && !!id) {
      getRet00cDetailRequest()
    }
  }, [id, open, mode])

  const formattedDetailRecord = useMemo(() => formatRet00cExampleFields(detailRecord), [detailRecord])

  const handleDestroy = async () => {
    setDestroying(true)

    try {
      await destroyRet00c(id)
      message.success('删除成功')

      if (mode === 'drawer') {
        onClose?.()
      }

      reloadTree()
      navigate('/ret00cs')
    } catch (error: any) {
      message.error(error?.response?.data?.message || '删除失败')
    } finally {
      setDestroying(false)
    }
  }

  const openNewPage = () => {
    const params = new URLSearchParams()
    if (formattedDetailRecord?.ret00cd_id) {
      params.set('ret00cd_id', String(formattedDetailRecord.ret00cd_id))
    }
    const query = params.toString()
    navigate(query ? `/ret00cs/new?${query}` : '/ret00cs/new')
  }

  const requestParams = ret00cas.filter((item) => item.param_scope !== 'response')
  const responseParams = ret00cas.filter((item) => item.param_scope === 'response')
  const requestParamRows = flattenParams(requestParams)
  const responseParamRows = flattenParams(responseParams)
  const hasRequestExample = !!formattedDetailRecord?.request_example?.trim()
  const hasResponseExample = !!formattedDetailRecord?.response_example?.trim()

  useEffect(() => {
    if (requestParams.length === 0 && hasRequestExample) {
      setRequestTabKey('request-example')
      return
    }

    setRequestTabKey('request-params')
  }, [requestParams.length, hasRequestExample, id])

  useEffect(() => {
    if (responseParams.length === 0 && hasResponseExample) {
      setResponseTabKey('response-example')
      return
    }

    setResponseTabKey('response-params')
  }, [responseParams.length, hasResponseExample, id])

  const paramColumns = [
    {
      title: '参数名',
      dataIndex: 'name',
      key: 'name',
      width: 180,
    },
    {
      title: '中文名',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (value: string) => value || '-',
    },
    {
      title: '位置',
      dataIndex: 'param_scope',
      key: 'param_scope',
    },
    {
      title: '类型',
      dataIndex: 'data_type',
      key: 'data_type',
    },
    {
      title: '必填',
      dataIndex: 'required',
      key: 'required',
      render: (value: boolean) => value ? '是' : '否',
    },
    {
      title: '启用',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (value: boolean) => value ? '是' : '否',
    },
    {
      title: '排序',
      dataIndex: 'sort_no',
      key: 'sort_no',
    },
    {
      title: '说明',
      dataIndex: 'description',
      key: 'description',
      render: (value: string) => value || '-',
    },
  ]

  const paramColumnsWithIndent = paramColumns.map((item: any) => {
    if (item.dataIndex !== 'name') {
      return item
    }

    return {
      ...item,
      render: (value: string, record: any) => (
        <span style={{ paddingLeft: `${(record.level || 0) * 20}px` }}>
          {record.parent_id ? <span className='text-gray-400 mr-2'>-</span> : null}
          {value}
        </span>
      ),
    }
  })

  const responseParamColumnsWithIndent = paramColumnsWithIndent.filter((item: any) => item.dataIndex !== 'param_scope')

  const logColumns = [
    {
      title: '请求号',
      dataIndex: 'request_no',
      key: 'request_no',
    },
    {
      title: '请求地址',
      dataIndex: 'request_url',
      key: 'request_url',
      render: (value: string) => value || '-',
    },
    {
      title: '状态码',
      dataIndex: 'response_status',
      key: 'response_status',
      render: (value: number) => value || '-',
    },
    {
      title: '结果',
      dataIndex: 'success',
      key: 'success',
      render: (value: boolean) => <Tag color={value ? 'green' : 'red'}>{value ? '成功' : '失败'}</Tag>,
    },
    {
      title: '耗时(ms)',
      dataIndex: 'duration_ms',
      key: 'duration_ms',
      render: (value: number) => value || '-',
    },
    {
      title: '错误信息',
      dataIndex: 'error_message',
      key: 'error_message',
      render: (value: string) => value || '-',
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value: string) => value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
  ]

  const detailContent = (
    <>
      <p className='text-lg font-bold py-2'>
        基础信息
        {formattedDetailRecord?.code ? <span className='ml-3 text-sm font-normal text-gray-500'>编码：{formattedDetailRecord.code}</span> : null}
      </p>
      <Detail
        card={false}
        bordered={false}
        layout='horizontal'
        colon={true}
        dataSource={formattedDetailRecord}
        dataColumns={dataColumns}
        column={3}
      />

      <Divider />

      <Tabs
        activeKey={requestTabKey}
        onChange={setRequestTabKey}
        items={[
          {
            key: 'request-params',
            label: '入参列表',
            children: (
              <Table
                rowKey='id'
                columns={paramColumnsWithIndent}
                dataSource={requestParamRows}
                locale={{ emptyText: '暂无请求参数' }}
                pagination={false}
                size='small'
                bordered
                scroll={{ x: 'max-content', y: 80 * 5 }}
              />
            ),
          },
          {
            key: 'request-example',
            label: '请求示例',
            children: (
              <pre className='bg-gray-50 border border-gray-200 rounded p-4 whitespace-pre-wrap break-all min-h-[120px]'>
                {formatJsonText(formattedDetailRecord?.request_example) || '暂无请求示例'}
              </pre>
            ),
          },
        ]}
      />

      <Tabs
        activeKey={responseTabKey}
        onChange={setResponseTabKey}
        items={[
          {
            key: 'response-params',
            label: '出参列表',
            children: (
              <Table
                rowKey='id'
                columns={responseParamColumnsWithIndent}
                dataSource={responseParamRows}
                locale={{ emptyText: '暂无返回参数' }}
                pagination={false}
                size='small'
                bordered
                scroll={{ x: 'max-content', y: 80 * 5 }}
              />
            ),
          },
          {
            key: 'response-example',
            label: '响应示例',
            children: (
              <pre className='bg-gray-50 border border-gray-200 rounded p-4 whitespace-pre-wrap break-all min-h-[120px]'>
                {formatJsonText(formattedDetailRecord?.response_example) || '暂无响应示例'}
              </pre>
            ),
          },
        ]}
      />

      <Divider />

      <p className='text-lg font-bold py-2'>调用日志</p>
      <Table
        rowKey='id'
        columns={logColumns}
        dataSource={ret00ccs}
        pagination={false}
        size='small'
        bordered
        locale={{ emptyText: '暂无调用日志' }}
        scroll={{ x: 'max-content', y: 80 * 5 }}
      />
    </>
  )

  if (mode === 'page') {
    return (
      <div className='w-full min-w-0'>
          <div
            className={`${RET00C_ACTION_BAR_WRAPPER_CLASS_NAME} ${RET00C_ACTION_BAR_LAYOUT_CLASS_NAME}`}
            style={RET00C_ACTION_BAR_WRAPPER_STYLE}
          >
            <div className={RET00C_ACTION_BAR_GROUP_CLASS_NAME}>
              <div className='flex-shrink-0'>
                <Ret00cActionBar
                  className='mb-0 border-0 bg-transparent px-0 py-0 rounded-none'
                  items={[
                    {
                      key: 'back-list',
                      label: '返回列表',
                      icon: <ArrowLeftOutlined />,
                      onClick: () => { navigate('/ret00cs') },
                    },
                    {
                      key: 'new',
                      label: '新增',
                      icon: <PlusOutlined />,
                      onClick: openNewPage,
                    },
                    {
                      key: 'edit',
                      label: '编辑',
                      icon: <EditOutlined />,
                      onClick: () => { navigate(`/ret00cs/${id}/edit`) },
                    },
                    {
                      key: 'delete',
                      label: '删除',
                      icon: <DeleteOutlined />,
                      danger: true,
                      loading: destroying,
                      onClick: handleDestroy,
                      confirm: {
                        title: '确定删除这个接口吗？',
                        description: '删除后接口、参数和调用日志将一并移除，且不可恢复。',
                        okText: '确认删除',
                        cancelText: '取消',
                      },
                    },
                  ]}
                />
            </div>
          </div>
            <div className='flex flex-1 justify-end' />
        </div>
        {detailContent}
      </div>
    )
  }

  return (
    <Drawer
      open={open}
      title="接口详情"
      onClose={onClose}
      width={1200}
      extra={(
        <Space>
          <Button icon={<PlusOutlined />} onClick={openNewPage}>
            新增
          </Button>
          <Popconfirm
            title='确定删除这个接口吗？'
            description='删除后接口、参数和调用日志将一并移除，且不可恢复。'
            onConfirm={handleDestroy}
            okText='确认删除'
            cancelText='取消'
          >
            <Button danger icon={<DeleteOutlined />} loading={destroying}>
              删除
            </Button>
          </Popconfirm>
          <Ret00cForm record={formattedDetailRecord} buttonText='编辑' buttonType='default' submitSuccess={() => getRet00cDetailRequest()} />
        </Space>
      )}
      destroyOnClose
    >
      {detailContent}
    </Drawer>
  )
}


export default Ret00cDetail
